import React, { useEffect, useState } from "react";
import { Loader2, BarChart3 } from "lucide-react";
import { fetchAdminStats } from "@/lib/api";
import AdminStatsTrend from "@/components/AdminStatsTrend";

const fmtIST = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", dateStyle: "medium", timeStyle: "short" }) : null);
const Num = ({ v }) => (v === null || v === undefined ? <span className="text-slate-400">Data unavailable</span> : v);

function Tile({ label, value, sub, testid }) {
  return (
    <div className="bg-white border border-slate-100 rounded-2xl p-4 min-w-0" data-testid={testid}>
      <div className="text-xs text-slate-500">{label}</div>
      <div className="font-display text-2xl font-bold text-slate-900 mt-0.5"><Num v={value} /></div>
      {sub && <div className="text-[11px] text-slate-500 mt-0.5 break-words">{sub}</div>}
    </div>
  );
}

function AdRow({ a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-slate-100 rounded-xl bg-white p-3 min-w-0" data-testid={`admin-stats-ad-${a.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-slate-900 break-words">{a.title || a.id}</h3>
          <div className="text-[11px] text-slate-500 break-words">{a.status}{a.running ? " · running" : ""} · PIN {a.pincodes.filter(Boolean).join(", ") || "—"}</div>
        </div>
        {a.by_pin.length > 0 && <button onClick={() => setOpen((o) => !o)} className="text-xs font-semibold text-emerald-700 min-h-[32px]" data-testid={`admin-stats-ad-toggle-${a.id}`}>{open ? "Hide PIN breakdown" : "PIN breakdown"}</button>}
      </div>
      <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
        {[["Delivered", a.delivered, "delivered"], ["Views", a.views, "views"], ["Clicks", a.clicks, "clicks"], ["WhatsApp clicks", a.whatsapp_clicks, "wa"]].map(([l, v, k]) => (
          <div key={k} className="rounded-lg bg-slate-50 px-2 py-1.5"><div className="text-slate-500">{l}</div><div className="font-semibold text-slate-900" data-testid={`admin-stats-ad-${k}-${a.id}`}>{v}</div></div>
        ))}
      </div>
      {open && (
        <div className="mt-2 overflow-x-auto">
          <table className="w-full text-xs whitespace-nowrap" data-testid={`admin-stats-ad-pins-${a.id}`}>
            <thead><tr className="text-slate-500 text-left"><th className="py-1 pe-3">PIN</th><th className="py-1 pe-3">Delivered</th><th className="py-1 pe-3">Views</th><th className="py-1 pe-3">Clicks</th><th className="py-1">WhatsApp</th></tr></thead>
            <tbody>{a.by_pin.map((p) => <tr key={p.pincode} className="border-t border-slate-100"><td className="py-1 pe-3 font-semibold">{p.pincode}</td><td className="py-1 pe-3">{p.delivered}</td><td className="py-1 pe-3">{p.views}</td><td className="py-1 pe-3">{p.clicks}</td><td className="py-1">{p.whatsapp_clicks}</td></tr>)}</tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// Admin-only actual counts from the database; nothing estimated.
export default function AdminStats() {
  const [s, setS] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => { fetchAdminStats().then(setS).catch(() => setErr(true)); }, []);
  if (err) return <div className="text-sm text-rose-700" data-testid="admin-stats-error">Data unavailable — could not load stats.</div>;
  if (!s) return <div className="text-sm text-slate-500 inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading stats…</div>;
  const since = fmtIST(s.views_tracking_since);
  return (
    <div className="space-y-4" data-testid="admin-stats">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Tile label="Published products" value={s.products} sub="in listed shops" testid="admin-stats-products" />
        <Tile label="Active services" value={s.services} sub="active & approved" testid="admin-stats-services" />
        <Tile label="Ads (all)" value={s.ads_total} sub={Object.entries(s.ads_by_status || {}).map(([k, v]) => `${k} ${v}`).join(" · ")} testid="admin-stats-ads" />
        <Tile label="Ads running now" value={s.ads_running} testid="admin-stats-ads-running" />
      </div>
      <div className="rounded-xl bg-slate-50 border border-slate-100 p-3 text-xs text-slate-600" data-testid="admin-stats-note">
        <span className="font-semibold text-slate-800">Delivered</span> = ad sent in listings/Notifications (not proof it was seen; older data). <span className="font-semibold text-slate-800">Views</span> = ad ≥50% on screen for 1+ second, max once per visitor per ad per 30 min{since ? `, recorded since ${since}` : ""}. Clicks and WhatsApp clicks are counted separately.
      </div>
      <AdminStatsTrend />
      <h2 className="font-display text-lg font-bold flex items-center gap-2"><BarChart3 className="w-4 h-4 text-emerald-700" /> Ad performance</h2>
      {s.ads.length === 0 ? <div className="text-sm text-slate-500">No advertisements yet.</div> : (
        <div className="space-y-2" data-testid="admin-stats-ads-list">{s.ads.map((a) => <AdRow key={a.id} a={a} />)}</div>
      )}
    </div>
  );
}
